import '../../Admin/admin-shared.css'
import './configurazioneNora.css'

import { useEffect, useState } from 'react'

import { PageHeader } from '../../../Components/Common/PageHeader'
import { useAuth } from '../../../Provider/AuthProvider'
import { useCQuery } from '../../../Services/QueryCache'
import { cap } from '../../../State'
import { translations } from '../../../translations'
import { useOverview } from './api'
import CollegamentoGoelett from './CollegamentoGoelett'
import EccezioniCommunity from './EccezioniCommunity'
import { environmentSelezionabili } from './Functions'
import RiepilogoEsistente from './RiepilogoEsistente'
import SchedaConfigurazione from './SchedaConfigurazione'
import Select from './Select'

const language = cap.language.state || 'IT'
const t = translations[language] || translations.IT

const STORAGE_KEY = 'cnEnvironmentId'

/**
 * Converte la scelta in numero: il server valida `environmentId` come intero,
 * quindi lo stato della pagina tiene un numero (null se vuoto).
 * @param {string|number|null} id - Valore grezzo (da select o localStorage)
 * @returns {number|null} Id numerico o null
 */
const ambienteNumerico = (id) => {
  if (id === '' || id === null || id === undefined) return null
  return Number.isInteger(Number(id)) ? Number(id) : null
}

/**
 * D28 — pagina «Configurazione Nora» (docs/nora/09 §9).
 *
 * Una sola pagina verticale, dall'uso quotidiano al raro: selettore environment,
 * configurazione predefinita (motore, viaggi, aggiunte), eccezioni per community,
 * collegamento Goelett (compatto finché nessuna configurazione lo usa) e valori letti da
 * altre fonti, richiusi. Una sola lettura (`overview`).
 *
 * @returns {import('react').JSX.Element} Pagina di configurazione
 */
export function ConfigurazioneNora() {
  const { user = {} } = useAuth()
  const { data: environments = [], isSuccess: isSuccessEnvironments } = useCQuery(['environments'])
  const [environmentId, setEnvironmentId] = useState(() => {
    let salvato = ''
    try {
      salvato = window.localStorage.getItem(STORAGE_KEY) || ''
    } catch {
      /* localStorage non disponibile */
    }
    return ambienteNumerico(salvato)
  })
  const { data: overview, isLoading, isError, refetch } = useOverview(environmentId)
  // Il collegamento Goelett aperto a mano o da «Configura il collegamento» di una scheda
  // che ha scelto Prenotazione Goelett: senza collegamento il server la rifiuta.
  const [goelettAperto, setGoelettAperto] = useState(false)

  const vaiA = (id) =>
    window.requestAnimationFrame(() =>
      document.getElementById(id)?.scrollIntoView?.({ behavior: 'smooth', block: 'start' })
    )
  const apriGoelett = () => {
    setGoelettAperto(true)
    vaiA('cn-goelett')
  }

  useEffect(() => {
    if (!isSuccessEnvironments || environmentId) return
    const ambiente = environments.find(
      ({ tomcatCode }) => user?.user?.environment?.tomcatCode === tomcatCode
    )
    const id = ambiente?.id
    if (id) setEnvironmentId(id)
  }, [isSuccessEnvironments, environments, user])

  const cambiaEnvironment = (id) => {
    setEnvironmentId(ambienteNumerico(id))
    setGoelettAperto(false)
    try {
      id
        ? window.localStorage.setItem(STORAGE_KEY, String(id))
        : window.localStorage.removeItem(STORAGE_KEY)
    } catch {
      /* localStorage non disponibile: la scelta resta solo in memoria */
    }
  }

  const environmentOptions = environmentSelezionabili(environments, user?.user)

  const messaggio = (testo, azione) => (
    <div className="ncard">
      <div className="ncard__body" style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
        <p style={{ margin: 0, color: 'var(--ink-500)' }}>{testo}</p>
        {azione}
      </div>
    </div>
  )

  const contenuto = () => {
    if (!environmentId) return messaggio(t.cnNessunEnvironment)
    if (isLoading) return messaggio(t.caricamento)
    if (isError || !overview) {
      return messaggio(
        t.cnCaricamentoFallito,
        <button type="button" className="nbtn nbtn--ghost nbtn--sm" onClick={() => refetch()}>
          {t.cnRiprova}
        </button>
      )
    }
    const card = overview.defaultConfiguration
    return (
      <div style={{ display: 'grid', gap: 16 }}>
        <SchedaConfigurazione
          key={`default-${overview.environment.id}-${card?.revision ?? 'nessuna'}`}
          overview={overview}
          card={card}
          communityPresetId={null}
          variante="default"
          onApriGoelett={apriGoelett}
        />
        <EccezioniCommunity overview={overview} onApriGoelett={apriGoelett} />
        <CollegamentoGoelett
          key={`goelett-${overview.environment.id}-${overview.providerStatus?.goelett?.revision ?? 'nessuna'}`}
          overview={overview}
          aperto={goelettAperto}
          onCambiaAperto={setGoelettAperto}
        />
        <RiepilogoEsistente overview={overview} />
      </div>
    )
  }

  return (
    <div className="admin-page--wide">
      <PageHeader title={t.cnTitolo} subtitle={t.cnSottotitolo} />

      <div className="nsec" style={{ marginBottom: 16 }}>
        <div className="nsec__body">
          <div className="nrow">
            <div className="nfield" style={{ gridColumn: 'span 4' }}>
              <label htmlFor="cn-environment">{t.cnSelezionaEnvironment}</label>
              <Select id="cn-environment" value={environmentId ?? ''} onChange={cambiaEnvironment}>
                <option value="">—</option>
                {environmentOptions.map(({ value, label }) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </Select>
            </div>
          </div>
        </div>
      </div>

      {contenuto()}
    </div>
  )
}

export default ConfigurazioneNora
